﻿import { useEffect } from 'react';
import { gsap } from 'gsap';
import { reducedMotion } from './useLenis.js';

export function useSpotlight(containerRef) {
  useEffect(() => {
    const scope = containerRef?.current ?? document;

    // 1. Stage spotlights
    const cleanups = [];

    const stages = scope.querySelectorAll('.stage');
    stages.forEach((stage) => {
      let light = stage.querySelector(':scope > .spotlight');
      if (!light) {
        light = document.createElement('div');
        light.className = 'spotlight';
        light.setAttribute('aria-hidden', 'true');
        stage.prepend(light);
      }

      if (reducedMotion()) return;

      const at = { x: 50, y: 50 };
      const apply = () => {
        light?.style.setProperty('--x', `${at.x}%`);
        light?.style.setProperty('--y', `${at.y}%`);
      };

      const toX = gsap.quickTo(at, 'x', {
        duration: 0.55,
        ease: 'power3.out',
        onUpdate: apply,
      });
      const toY = gsap.quickTo(at, 'y', {
        duration: 0.55,
        ease: 'power3.out',
        onUpdate: apply,
      });

      const onPointerMove = (event) => {
        const box = stage.getBoundingClientRect();
        toX(((event.clientX - box.left) / box.width) * 100);
        toY(((event.clientY - box.top) / box.height) * 100);
        stage.dataset.lit = 'true';
      };

      const onPointerLeave = () => {
        stage.dataset.lit = 'false';
      };

      stage.addEventListener('pointermove', onPointerMove);
      stage.addEventListener('pointerleave', onPointerLeave);

      cleanups.push(() => {
        stage.removeEventListener('pointermove', onPointerMove);
        stage.removeEventListener('pointerleave', onPointerLeave);
      });
    });

    // 2. Cell spotlights
    const cells = scope.querySelectorAll('.cell');
    cells.forEach((cell) => {
      let fill = cell.querySelector(':scope > .cell-fill');
      if (!fill) {
        fill = document.createElement('div');
        fill.className = 'cell-fill';
        fill.setAttribute('aria-hidden', 'true');
        cell.prepend(fill);
      }

      const onPointerMove = (event) => {
        const box = cell.getBoundingClientRect();
        cell.style.setProperty('--mx', `${event.clientX - box.left}px`);
        cell.style.setProperty('--my', `${event.clientY - box.top}px`);
      };

      cell.addEventListener('pointermove', onPointerMove);
      cleanups.push(() => {
        cell.removeEventListener('pointermove', onPointerMove);
      });
    });

    // 3. Magnetic buttons
    const btns = scope.querySelectorAll('.btn:not(.btn--bare)');
    btns.forEach((btn) => {
      if (reducedMotion()) return;

      const toX = gsap.quickTo(btn, 'x', { duration: 0.35, ease: 'power2.out' });
      const toY = gsap.quickTo(btn, 'y', { duration: 0.35, ease: 'power2.out' });

      const onPointerMove = (event) => {
        const box = btn.getBoundingClientRect();
        const cx = box.left + box.width / 2;
        const cy = box.top + box.height / 2;
        toX((event.clientX - cx) * 0.22);
        toY((event.clientY - cy) * 0.22);
      };

      const onPointerLeave = () => {
        toX(0);
        toY(0);
      };

      btn.addEventListener('pointermove', onPointerMove);
      btn.addEventListener('pointerleave', onPointerLeave);

      cleanups.push(() => {
        btn.removeEventListener('pointermove', onPointerMove);
        btn.removeEventListener('pointerleave', onPointerLeave);
      });
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [containerRef]);
}